import {
  createBrowserRouter,
  Outlet,
  RouterProvider,
  useRouteError,
} from 'react-router-dom';
import { useError } from '@hooks/useError';
import { BaseError, useAppContext, useConfigContext } from 'submodules/fe-core';
import { useAtom } from 'jotai';
import { feedbackUIAvailabilityAtom } from '@atoms';
import { PageNotFound } from '@components/PageNotFound';
import { CatalogRouteLoader } from '@routes/catalog/CatalogRouteLoader';
import { useTeams } from '@fe-learner-common/hooks/data/useTeams';
import { SessionDetailsRouteLoader } from '@routes/session-details/SessionDetailsRouteLoader';
import { SelfRegRouteLoader } from '@routes/selfreg/SelfRegRouteLoader';
import { ReactNode } from 'react';
import { usePageEvent } from '@fe-learner-common/hooks/usePageEvent';
import { Footer } from '@fe-learner-common/components/Footer';

const RouteError = () => {
  const handleContextualError = useError();
  const [feedbackUIAvailable] = useAtom(feedbackUIAvailabilityAtom);

  const error = useRouteError() as BaseError;
  handleContextualError({
    error: error,
    errorMapOverride: {
      type: 'page',
    },
  });

  return feedbackUIAvailable ? (
    <div>Route error - Feedback UI available</div>
  ) : (
    <div>RouteError - Feedback UI not available</div>
  );
};

const Wrapper = ({ children }: { children?: ReactNode }) => {
  const { isLoading } = useAppContext();
  usePageEvent();

  return (
    <>
      {children || <Outlet />}
      <Footer className="lf:mt-auto lf:pt-[200px]" isLoading={isLoading} />
    </>
  );
};

export const AppRouter = () => {
  const { extension, config } = useConfigContext();
  const { error: appError, isError: isAppError } = useAppContext();

  const baseName = extension?.microfrontend?.basename || '';

  // initialize teams data as early as possible.
  useTeams(config?.loggedIn && config?.webfeatures?.['teamview']);

  if (isAppError) {
    throw appError;
  }

  const router = createBrowserRouter(
    [
      {
        errorElement: <RouteError />,
        element: <Wrapper />,
        children: [
          {
            path: '/',
            element: <CatalogRouteLoader />,
          },
          {
            path: '/session/:sessionId/*',
            element: <SessionDetailsRouteLoader />,
          },
          {
            path: '/selfreg/:sessionId/*',
            element: <SelfRegRouteLoader />,
          },
        ],
      },
      {
        path: '*',
        element: <PageNotFound />,
      },
    ],
    {
      basename: import.meta.env.MODE === 'development' ? '' : baseName,
    },
  );

  return <RouterProvider router={router} />;
};
